import { PasseriniAPI } from '@/services/apiService';
import type {
  CreateRetiroPayload,
  CreateRetiroResponse,
  ListRetirosResponse,
  RetiroCopia,
  RetiradorPayload,
  UpdateRetiroPayload,
  UpdateRetiroResponse,
  DeleteRetiroResponse,
  UpdateRetiradorPayload,
  RetiroMasivoResponse,
} from '../types';

/**
 * Servicio para gestionar retiros de copias
 */
export const retiroCopiasService = {
  /**
   * Registrar un nuevo retiro de copias
   */
  async registrarRetiro(
    otroTramiteId: string,
    fechaRetiro: string,
    retiradores: RetiradorPayload[]
  ): Promise<CreateRetiroResponse> {
    const formData = new FormData();

    // Agregar fecha de retiro
    formData.append('fecha_retiro', fechaRetiro);

    // Agregar datos de cada retirador
    retiradores.forEach((retirador, index) => {
      formData.append(`retiradores[${index}][nombre_completo]`, retirador.nombreCompleto);
      formData.append(`retiradores[${index}][cantidad_copias]`, String(retirador.cantidadCopias));
      formData.append(`retiradores[${index}][firma_digital]`, retirador.firmaDigital);
    });

    const response = await PasseriniAPI.post(
      `/otros-tramites/${otroTramiteId}/retiros`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    return response.data;
  },

  /**
   * Listar todos los retiros de un trámite
   */
  async listarRetiros(otroTramiteId: string): Promise<ListRetirosResponse> {
    const response = await PasseriniAPI.get(`/otros-tramites/${otroTramiteId}/retiros`);
    return response.data;
  },

  /**
   * Obtener detalle de un retiro específico
   */
  async getRetiroById(retiroId: string): Promise<RetiroCopia> {
    const response = await PasseriniAPI.get(`/retiros/${retiroId}`);
    return response.data;
  },

  /**
   * Actualizar un retiro existente
   */
  async updateRetiro(payload: UpdateRetiroPayload): Promise<UpdateRetiroResponse> {
    const formData = new FormData();

    // Agregar fecha de retiro si cambió
    if (payload.fechaRetiro) {
      formData.append('fecha_retiro', payload.fechaRetiro);
    }

    // Agregar datos de cada retirador
    payload.retiradores.forEach((retirador, index) => {
      // ID del retirador (si existe, es update; si no, es create nuevo)
      if (retirador.id) {
        formData.append(`retiradores[${index}][id]`, retirador.id);
      }

      formData.append(`retiradores[${index}][nombre_completo]`, retirador.nombreCompleto);
      formData.append(`retiradores[${index}][cantidad_copias]`, String(retirador.cantidadCopias));

      if (retirador.firmaDigital) {
        formData.append(`retiradores[${index}][firma_digital]`, retirador.firmaDigital);
      } else if (retirador.keepFirmaDigital) {
        formData.append(`retiradores[${index}][keep_firma_digital]`, '1');
      }
    });

    // Laravel no soporta PUT con FormData directamente, usar POST con _method
    formData.append('_method', 'PUT');

    const response = await PasseriniAPI.post(
      `/retiros/${payload.retiroId}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    return response.data;
  },

  /**
   * Registrar el mismo retiro para varios trámites (retiro masivo).
   * Se retiran TODAS las copias disponibles de cada trámite seleccionado.
   * La operación es atómica: si uno falla, no se procesa ninguno.
   */
  async registrarRetiroMasivo(
    otroTramiteIds: string[],
    fechaRetiro: string,
    retirador: { nombreCompleto: string; firmaDigital?: Blob }
  ): Promise<RetiroMasivoResponse> {
    const formData = new FormData();

    otroTramiteIds.forEach((id) => {
      formData.append('otro_tramite_ids[]', id);
    });

    formData.append('fecha_retiro', fechaRetiro);
    formData.append('nombre_completo', retirador.nombreCompleto);

    if (retirador.firmaDigital) {
      formData.append('firma_digital', retirador.firmaDigital, 'firma.png');
    }

    const response = await PasseriniAPI.post('/otros-tramites/retiros/masivo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });

    return response.data;
  },

  /**
   * Eliminar un retiro
   */
  async deleteRetiro(retiroId: string): Promise<DeleteRetiroResponse> {
    const response = await PasseriniAPI.delete(`/retiros/${retiroId}`);
    return response.data;
  },
};

export type RetiroCopiasService = typeof retiroCopiasService;

export default retiroCopiasService;
